import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { OfficialBadge, CommunityBadge } from '../../components/common/Badges';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Search, MapPin, Users, Loader2, SlidersHorizontal, X, History, Calendar, Clock, Radio, Settings, CheckCircle2 } from 'lucide-react';
import { formatEventDate } from '../../lib/formatters';
import { useUnits, formatEventDistance } from '../../lib/units';
import { RunBadgeRow } from '../../components/common/RunBadges';
import { useAuth } from '../../lib/auth-context';
import { organizerEventUrl } from '../../lib/organizer-url';

export default function MobileEvents() {
  const [q, setQ] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState('all');     // 'all' | 'official' | 'community'
  const [filterLocation, setFilterLocation] = useState(''); // free-text contains
  const [filterFrom, setFilterFrom] = useState('');         // YYYY-MM-DD
  const [filterTo, setFilterTo] = useState('');             // YYYY-MM-DD

  const { units } = useUnits();
  const { user } = useAuth();
  const { data: events, loading } = useApi(api.getEvents);
  const { data: pastEvents, loading: pastLoading } = useApi(
    () => api.getMyEvents({ past: true }),
    [],
  );
  // Set of event IDs the user has joined (upcoming + live) — used to surface
  // a "Registered" pill on each card across the Discover list.
  const { data: myUpcomingEvents } = useApi(() => api.getMyEvents({ past: false }), []);
  const joinedIds = useMemo(() => new Set((myUpcomingEvents || []).map((e) => e.id)), [myUpcomingEvents]);

  const filtered = useMemo(() => {
    const list = events || [];
    const ql = q.trim().toLowerCase();
    const fromTs = filterFrom ? new Date(filterFrom).setHours(0, 0, 0, 0) : null;
    const toTs = filterTo ? new Date(filterTo).setHours(23, 59, 59, 999) : null;
    const locQ = filterLocation.trim().toLowerCase();
    return list.filter((e) => {
      if (ql && !(e.name?.toLowerCase().includes(ql) || e.location?.toLowerCase().includes(ql))) return false;
      if (filterType !== 'all' && e.type !== filterType) return false;
      if (locQ && !(e.location || '').toLowerCase().includes(locQ)) return false;
      const t = new Date(e.date).getTime();
      if (fromTs && t < fromTs) return false;
      if (toTs && t > toTs) return false;
      return true;
    });
  }, [events, q, filterType, filterLocation, filterFrom, filterTo]);

  const official = filtered.filter((e) => e.type === 'official');
  const community = filtered.filter((e) => e.type === 'community');
  const activeFiltersCount = [
    filterType !== 'all',
    !!filterLocation,
    !!filterFrom,
    !!filterTo,
  ].filter(Boolean).length;

  return (
    <div className="px-4 pt-4 pb-6 space-y-4" data-testid="mobile-events">
      <h1 className="font-display text-3xl font-bold tracking-tight">Events</h1>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="events-search"
            placeholder="Search events or locations"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11 rounded-md"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          data-testid="events-filters-toggle"
          className={`relative h-11 w-11 rounded-md border flex items-center justify-center transition-colors ${
            showFilters || activeFiltersCount > 0 ? 'border-primary text-primary bg-primary/5' : 'border-border'
          }`}
          aria-label="Filters"
        >
          <SlidersHorizontal className="h-4 w-4" />
          {activeFiltersCount > 0 && (
            <span
              className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center"
              data-testid="events-active-filters-count"
            >
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {showFilters && (
        <FiltersPanel
          filterType={filterType} setFilterType={setFilterType}
          filterLocation={filterLocation} setFilterLocation={setFilterLocation}
          filterFrom={filterFrom} setFilterFrom={setFilterFrom}
          filterTo={filterTo} setFilterTo={setFilterTo}
          onClear={() => { setFilterType('all'); setFilterLocation(''); setFilterFrom(''); setFilterTo(''); }}
        />
      )}

      <Tabs defaultValue="all">
        <TabsList className="grid grid-cols-4 h-auto p-1 rounded-md">
          <TabsTrigger value="all" data-testid="events-tab-all" className="text-[10px] uppercase tracking-widest">All</TabsTrigger>
          <TabsTrigger value="official" data-testid="events-tab-official" className="text-[10px] uppercase tracking-widest">Official</TabsTrigger>
          <TabsTrigger value="community" data-testid="events-tab-community" className="text-[10px] uppercase tracking-widest">Community</TabsTrigger>
          <TabsTrigger value="past" data-testid="events-tab-past" className="text-[10px] uppercase tracking-widest gap-1">
            <History className="h-3 w-3" /> Past
          </TabsTrigger>
        </TabsList>

        {loading && <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}

        <TabsContent value="all" className="mt-4 space-y-3">
          {filtered.map((e) => <EventListCard key={e.id} event={e} units={units} isMine={e.organizer_id === user?.id} isJoined={joinedIds.has(e.id)} />)}
          {!loading && filtered.length === 0 && <Empty />}
        </TabsContent>
        <TabsContent value="official" className="mt-4 space-y-3">
          {official.map((e) => <EventListCard key={e.id} event={e} units={units} isMine={e.organizer_id === user?.id} isJoined={joinedIds.has(e.id)} />)}
          {!loading && official.length === 0 && <Empty />}
        </TabsContent>
        <TabsContent value="community" className="mt-4 space-y-3">
          {community.map((e) => <EventListCard key={e.id} event={e} units={units} isMine={e.organizer_id === user?.id} isJoined={joinedIds.has(e.id)} />)}
          {!loading && community.length === 0 && <Empty />}
        </TabsContent>
        <TabsContent value="past" className="mt-4 space-y-3" data-testid="events-past-list">
          {pastLoading && <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {!pastLoading && (pastEvents || []).length === 0 && (
            <div className="rivl-card p-8 text-center">
              <History className="h-7 w-7 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-3">No past events yet. Once you finish a race, it'll appear here with your badges.</p>
            </div>
          )}
          {!pastLoading && (pastEvents || []).map((e) => (
            <PastEventCard key={e.id} event={e} units={units} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FiltersPanel({
  filterType, setFilterType,
  filterLocation, setFilterLocation,
  filterFrom, setFilterFrom,
  filterTo, setFilterTo,
  onClear,
}) {
  return (
    <div className="rivl-card p-4 space-y-3" data-testid="events-filters-panel">
      <div className="flex items-center justify-between">
        <p className="rivl-label text-[10px]">Filters</p>
        <button
          type="button"
          onClick={onClear}
          className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1"
          data-testid="events-filters-clear"
        >
          <X className="h-3 w-3" /> Clear all
        </button>
      </div>

      <div>
        <p className="rivl-label text-[9px] mb-1.5">Type</p>
        <div className="grid grid-cols-3 gap-2">
          {['all', 'official', 'community'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setFilterType(t)}
              data-testid={`events-filter-type-${t}`}
              className={`h-9 rounded-md border-2 text-[10px] uppercase tracking-widest font-semibold ${
                filterType === t ? 'border-primary bg-primary/5 text-primary' : 'border-border'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="rivl-label text-[9px] mb-1.5">Location</p>
        <div className="relative">
          <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            placeholder="e.g. Lagos"
            className="pl-9 h-10 rounded-md"
            data-testid="events-filter-location"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="rivl-label text-[9px] mb-1.5">From</p>
          <Input
            type="date"
            value={filterFrom}
            onChange={(e) => setFilterFrom(e.target.value)}
            className="h-10 rounded-md text-sm"
            data-testid="events-filter-from"
          />
        </div>
        <div>
          <p className="rivl-label text-[9px] mb-1.5">To</p>
          <Input
            type="date"
            value={filterTo}
            onChange={(e) => setFilterTo(e.target.value)}
            className="h-10 rounded-md text-sm"
            data-testid="events-filter-to"
          />
        </div>
      </div>
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-muted-foreground py-8 text-center">No events match.</p>;
}

function EventListCard({ event, units, isMine, isJoined }) {
  return (
    <div
      data-testid={`event-card-${event.id}`}
      className="rivl-card overflow-hidden hover:border-primary transition-colors relative"
    >
      <Link to={`/events/${event.id}`} className="flex">
        <div className="w-28 shrink-0 bg-cover bg-center" style={{ backgroundImage: `url(${event.image})` }} />
        <div className="flex-1 p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-display font-semibold leading-tight">{event.name}</div>
              <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{event.eventMode === 'virtual' ? 'Virtual' : (event.location || '—')}</span>
                <span className="flex items-center gap-1"><Users className="h-3 w-3" />{event.participants}</span>
              </div>
            </div>
            {event.type === 'official' ? <OfficialBadge /> : <CommunityBadge />}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <div className="font-mono text-xs">{formatEventDistance(event.distanceKm, units)}</div>
            <div className="text-[11px] text-muted-foreground">· {formatEventDate(event.date)}</div>
            {event.raceState === 'live' && (
              <span
                data-testid="event-card-live"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 h-5 rounded-full bg-success text-white"
              >
                <Radio className="h-2.5 w-2.5 animate-pulse" /> Live
              </span>
            )}
            {event.joinDeadline && <DeadlinePill deadline={event.joinDeadline} />}
          </div>
        </div>
      </Link>
      {isJoined && (
        <span
          data-testid={`event-card-registered-${event.id}`}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-2 h-5 rounded-full bg-success/15 text-success border border-success/30"
        >
          <CheckCircle2 className="h-2.5 w-2.5" /> Registered
        </span>
      )}
      {isMine && (
        <div className="px-3 pb-2.5 -mt-1 flex justify-end">
          <a
            href={organizerEventUrl(event.id)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`event-card-manage-${event.id}`}
            className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
            title="Manage in organizer portal"
          >
            <Settings className="h-2.5 w-2.5" /> Manage event
          </a>
        </div>
      )}
    </div>
  );
}

function DeadlinePill({ deadline }) {
  const ts = new Date(deadline).getTime();
  const ms = ts - Date.now();
  const passed = ms < 0;
  // Highlight when within 24h or already past.
  const soon = ms > 0 && ms < 86400000;
  let label;
  if (passed) label = 'Registration closed';
  else if (soon) {
    const hours = Math.max(1, Math.floor(ms / 3600000));
    label = `Closes in ${hours}h`;
  } else {
    label = `Closes ${formatEventDate(deadline)}`;
  }
  return (
    <span
      data-testid="event-card-deadline"
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 h-5 rounded-full ${
        passed
          ? 'bg-destructive/10 text-destructive border border-destructive/30'
          : soon
            ? 'bg-primary/10 text-primary border border-primary/30'
            : 'bg-secondary text-muted-foreground border border-border'
      }`}
    >
      <Clock className="h-2.5 w-2.5" /> {label}
    </span>
  );
}

function PastEventCard({ event, units }) {
  return (
    <div
      className="rivl-card overflow-hidden"
      data-testid={`past-event-card-${event.id}`}
    >
      <Link to={`/events/${event.id}`} className="flex hover:bg-secondary/30 transition-colors">
        <div className="w-24 shrink-0 bg-cover bg-center" style={{ backgroundImage: `url(${event.image})` }} />
        <div className="flex-1 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-display font-semibold leading-tight truncate">{event.name}</div>
              <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatEventDate(event.date)}</span>
                <span className="font-mono">{formatEventDistance(event.distanceKm, units)}</span>
              </div>
            </div>
            {event.type === 'official' ? <OfficialBadge /> : <CommunityBadge />}
          </div>
        </div>
      </Link>
      <div className="px-3 py-2 border-t border-border">
        <RunBadgeRow event={event} />
      </div>
    </div>
  );
}
